import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  readonly visible = signal(false);
  readonly installing = signal(false);
  readonly showManualInstructions = signal(false);
  readonly iosDevice = signal(false);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.iosDevice.set(this.isIosDevice());
    this.showIfEligible();

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
    };

    const onInstalled = () => {
      this.deferredPrompt = null;
      this.visible.set(false);
      this.showManualInstructions.set(false);
    };

    const onPageShow = () => {
      this.showIfEligible();
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('pageshow', onPageShow);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('pageshow', onPageShow);
    });
  }

  dismiss(): void {
    this.visible.set(false);
    this.showManualInstructions.set(false);
  }

  async install(): Promise<void> {
    const event = this.deferredPrompt;
    if (!event) {
      this.showManualInstructions.set(true);
      return;
    }

    this.installing.set(true);
    try {
      await event.prompt();
      await event.userChoice;
      this.deferredPrompt = null;
      this.visible.set(false);
      this.showManualInstructions.set(false);
    } finally {
      this.installing.set(false);
    }
  }

  private showIfEligible(): void {
    if (this.isRunningInstalled() || !this.isMobileBrowser()) {
      this.visible.set(false);
      return;
    }

    this.visible.set(true);
  }

  private isRunningInstalled(): boolean {
    const nav = window.navigator as Navigator & { standalone?: boolean };
    if (nav.standalone === true) {
      return true;
    }

    return (
      this.displayModeMatches('standalone') ||
      this.displayModeMatches('fullscreen') ||
      this.displayModeMatches('minimal-ui')
    );
  }

  private displayModeMatches(mode: string): boolean {
    return window.matchMedia(`(display-mode: ${mode})`).matches;
  }

  private isMobileBrowser(): boolean {
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(
      window.navigator.userAgent,
    );
  }

  private isIosDevice(): boolean {
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  }
}
